import { capitalize } from 'helpers/utils';
import { Fragment, useEffect, useState } from 'react';
import { Dropdown, Nav } from 'react-bootstrap';
import { RouteItems, getRoutes } from 'sitemap';
import TopNavMegaMenu from './TopNavMegaMenu';
import TopNavItem from './TopNavItem';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { getCookie, setCookie } from 'app/shared/utils/cookie.utils';

const ACTIVE_PRODUCT_COOKIE = 'activeProduct';

const NavbarTopNav = () => {
  const [routes, setRoutes] = useState(getRoutes());
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [activeProduct, setActiveProduct] = useState<string | null>(() => getCookie(ACTIVE_PRODUCT_COOKIE));

  // Refresh routes when pathname changes (e.g. after login) so Create New / All Projects get current userId
  useEffect(() => {
    if (pathname.startsWith('/mtc+')) {
      setCookie(ACTIVE_PRODUCT_COOKIE, 'MTC+');
      setActiveProduct('MTC+');
    } else if (pathname.startsWith('/ate+')) {
      setCookie(ACTIVE_PRODUCT_COOKIE, 'ATE+');
      setActiveProduct('ATE+');
    } else if (pathname.startsWith('/csa+')) {
      setCookie(ACTIVE_PRODUCT_COOKIE, 'CSA+');
      setActiveProduct('CSA+');
    } else if (pathname.startsWith('/project')) {
      setCookie(ACTIVE_PRODUCT_COOKIE, 'TSC+');
      setActiveProduct('TSC+');
    }
    setRoutes(getRoutes());
  }, [pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRoutes(getRoutes());
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const userId = localStorage.getItem('userId') || '';
  const handleProductSelect = (productId: string) => {
    setCookie(ACTIVE_PRODUCT_COOKIE, productId);
    setActiveProduct(productId);
    localStorage.removeItem('projectId');
    if (productId === 'CSA+') {
      navigate('/csa+/all-projects');
    } else if (productId === 'MTC+') {
      navigate('/mtc+/all-projects');
    } else if (productId === 'ATE+') {
      navigate('/ate+/all-projects');
    } else if (productId === 'TSC+') {
      navigate(`/project/${userId}/all-projects`);
    }
  };

  return (
    <Nav className="navbar-nav-top pb-4 pb-lg-0 scrollbar align-items-center gap-4">
      {routes.map(route => (
        <Fragment key={route.label}>
          <NavbarTopNavItem route={route} onProductSelect={route.label === 'Admin' ? handleProductSelect : undefined} />
          {route.label === 'Admin' && activeProduct && (
            <Nav.Item as="li" className="d-flex align-items-center">
              <button
                type="button"
                onClick={() => handleProductSelect(activeProduct)}
                className="border-0 px-2 py-1 rounded fw-semibold d-flex align-items-center"
                style={{
                  backgroundColor: '#03b5e8',
                  color: 'white',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                {activeProduct}
              </button>
            </Nav.Item>
          )}
        </Fragment>
      ))}
    </Nav>
  );
};

const NavbarTopNavItem = ({
  route,
  onProductSelect
}: {
  route: RouteItems;
  onProductSelect?: (productId: string) => void;
}) => {
  const Icon = route.icon;
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();

  const hasSubmenu =
    (route.pages && route.pages.length > 0) || (route.megaMenuSections && route.megaMenuSections.length > 0);
  const useMegaMenu = hasSubmenu;
  const isSimpleLink = !hasSubmenu && route.path;

  // Close when navigating (e.g. after clicking a menu link)
  useEffect(() => {
    if (show) setShow(false);
  }, [pathname]);

  if (isSimpleLink) {
    return (
      <Nav.Item as="li">
        <Nav.Link as={Link} to={route.path!} className="nav-link lh-1 d-flex align-items-center">
          <Icon className="me-2" size={16} />
          <span>{capitalize(route.horizontalNavLabel || route.label)}</span>
        </Nav.Link>
      </Nav.Item>
    );
  }

  return (
    <Dropdown
      as="li"
      show={show}
      className={classNames('nav-item', { 'position-static': useMegaMenu })}
      key={route.label}
      autoClose={true}
      onToggle={next => setShow(!!next)}
    >
      <Dropdown.Toggle
        as="a"
        variant=""
        className={classNames('nav-link lh-1 d-flex align-items-center cursor-pointer mega-nav-link', { show })}
      >
        <Icon className="me-2" size={16} />
        <span>{capitalize(route.horizontalNavLabel || route.label)}</span>
      </Dropdown.Toggle>
      {useMegaMenu ? (
        <TopNavMegaMenu
          route={route}
          onCloseMenu={() => setShow(false)}
          show={show}
          onProductSelect={onProductSelect}
        />
      ) : (
        <TopNavItem route={route} />
      )}
    </Dropdown>
  );
};

export default NavbarTopNav;
